import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { setToken, authHeaders } from '../lib/auth'
import { useStore } from '../hooks/useStore'
import { PixeCard } from '../components/ui/PixeCard'
import NavBar from '../components/layout/NavBar'
import StoreFilterDrawer from '../components/store/StoreFilterDrawer'
import type { StoreFilters } from '../types/store'
import { DEFAULT_STORE_FILTERS } from '../types/store'
import MobileStoreActionBar from '../components/pixe/MobileStoreActionBar'
import CommentsDrawer from '../components/comments/CommentsDrawer'
import SensitiveMedia from '../components/content/SensitiveMedia'

export default function Store() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qParam = searchParams.get('q') || ''
  const [q, setQ] = useState(qParam)
  useEffect(() => { setQ(qParam) }, [qParam])

  const [sort, setSort] = useState<'popular' | 'newest'>('newest')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState<StoreFilters>({ ...DEFAULT_STORE_FILTERS, sort })
  useEffect(() => { setFilters((p) => ({ ...p, sort })) }, [sort])

  const [errorLocal, setErrorLocal] = useState<string | null>(null)
  const { items, error } = useStore(q, { sort, filters })
  const errorMsg = errorLocal ?? error

  const hasActiveFilters = !!(
    (q && q.trim()) ||
    filters.category ||
    filters.condition ||
    filters.inStockOnly ||
    (typeof filters.priceMin === 'number') ||
    (typeof filters.priceMax === 'number')
  )

  const [popular, setPopular] = useState<any[] | null>(null)
  // Preload popular (top by likes_count) for the desktop rail.
  useEffect(() => {
    if (popular) return
    ;(async () => {
      try {
        const res = await fetch('/api/pixe_store/store/listings/')
        const data = await res.json()
        const arr = Array.isArray(data?.results) ? data.results : []
        const sorted = [...arr].sort((a: any, b: any) => (b?.likes_count || 0) - (a?.likes_count || 0)).slice(0, 9)
        setPopular(sorted)
      } catch {}
    })()
  }, [popular])

  // Mobile fullscreen store feed state (TikTok mode).
  const mobileFeedRef = useRef<HTMLDivElement | null>(null)
  const tapRef = useRef<{ x: number; y: number; moved: boolean; t: number } | null>(null)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentsListingId, setCommentsListingId] = useState<string | null>(null)

  // Restore mobile scroll after returning from store detail.
  useEffect(() => {
    const root = mobileFeedRef.current
    if (!root) return
    if (!items) return
    try {
      const raw = sessionStorage.getItem('mobileStore:scrollTop')
      if (!raw) return
      const top = Number(raw)
      if (!Number.isFinite(top)) return
      root.scrollTop = top
      sessionStorage.removeItem('mobileStore:scrollTop')
    } catch {}
  }, [items])

  return (
    <div className="min-h-screen">
      <NavBar mobileFixed />

      {/* Phone mode (< md): TikTok-style fullscreen Store feed */}
      <div className="md:hidden fixed inset-0 bg-black">
        {/* Filter icon (chips hidden on mobile) */}
        <div
          className="fixed right-3 z-30"
          style={{
            top: 'calc(var(--nav-h, 56px) + 10px)',
          }}
        >
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setFilterOpen(true)}
            className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-white/10 bg-black/30 backdrop-blur hover:bg-black/40"
            title="Filter Store"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/90" aria-hidden="true">
              <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z" />
            </svg>
          </button>
        </div>

        <div
          ref={mobileFeedRef}
          className="h-[100dvh] w-full overflow-y-auto snap-y snap-mandatory overscroll-contain no-scrollbar"
        >
          {(items ?? []).map((l: any, i: number) => {
            const img = l?.images?.[0]?.public_url || l?.product?.thumbnail_media?.public_url || null
            const warning = l?.content_warning || l?.images?.[0]?.content_warning || l?.product?.thumbnail_media?.content_warning || null
            const title = l?.product?.title || 'Listing'
            const subtitle = l?.product?.subtitle || ''
            const href = `/store/i/${l.public_id}`
            const handle = l?.seller?.handle ? `@${String(l.seller.handle).replace(/^@/, '')}` : null
            const meta = handle ? `Sold by ${handle}` : null
            const price = fmtPixe(l?.price_pixe)

            const sellerAvatarUrl = (l?.seller?.avatar || l?.seller?.avatar_url || null) as string | null
            const sellerProfilePublicId = (l?.seller?.profile_public_id || l?.seller?.public_id || null) as string | null

            return (
              <div
                key={l.public_id ?? i}
                className="snap-start h-[100dvh] w-full relative bg-black"
                data-feed-item="1"
                data-index={i}
                onPointerDown={(e) => {
                  const t = e.target as HTMLElement
                  if (t?.closest?.('button,a,input,textarea,select,label,[role="button"],[data-no-feed-tap="1"]')) return
                  tapRef.current = { x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
                }}
                onPointerMove={(e) => {
                  const s = tapRef.current
                  if (!s) return
                  const dx = Math.abs(e.clientX - s.x)
                  const dy = Math.abs(e.clientY - s.y)
                  if (dx > 12 || dy > 12) s.moved = true
                }}
                onPointerUp={(e) => {
                  const s = tapRef.current
                  tapRef.current = null
                  if (!s) return
                  const dt = Date.now() - s.t
                  if (s.moved || dt > 650) return
                  const t = e.target as HTMLElement
                  if (t?.closest?.('button,a,input,textarea,select,label,[role="button"],[data-no-feed-tap="1"]')) return
                  try {
                    if (mobileFeedRef.current) sessionStorage.setItem('mobileStore:scrollTop', String(mobileFeedRef.current.scrollTop))
                  } catch {}
                  navigate(href, { state: { fromMobileStore: true } as any })
                }}
              >
                {img || warning ? (
                  <SensitiveMedia imageUrl={img} warning={warning} alt={`${title} cover image`} className="absolute inset-0 h-full w-full" />
                ) : (
                  <div className="absolute inset-0 bg-white/5" />
                )}

                {/* Scrims for text readability */}
                <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/55 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 to-transparent" />

                {/* Price badge (top-right) */}
                {price ? (
                  <div className="absolute top-3 right-3 rounded-full bg-black/45 border border-white/10 px-3 py-1 text-xs font-semibold text-white/90 backdrop-blur">
                    {price}
                  </div>
                ) : null}

                {/* Metadata overlay */}
                <div className="absolute left-0 right-0 bottom-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                  <div className="max-w-[75%]">
                    <div className="text-xl font-extrabold leading-tight drop-shadow">{title}</div>
                    {meta ? <div className="mt-1 text-sm text-white/85 drop-shadow">{meta}</div> : null}
                    {subtitle ? <div className="mt-2 text-sm text-white/80 max-h-10 overflow-hidden drop-shadow">{subtitle}</div> : null}
                    <div className="mt-3 inline-flex items-center rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-xs text-white/90 backdrop-blur">
                      Tap to open product
                    </div>
                  </div>
                </div>

                <MobileStoreActionBar
                  listingId={String(l.public_id)}
                  sellerAvatarUrl={sellerAvatarUrl}
                  sellerProfilePublicId={sellerProfilePublicId}
                  shareUrl={l?.share_url || null}
                  initialLiked={Boolean(l?.liked)}
                  initialSaved={Boolean(l?.is_saved)}
                  initialLikes={typeof l?.likes_count === 'number' ? l.likes_count : undefined}
                  initialSaves={typeof l?.saves_count === 'number' ? l.saves_count : undefined}
                  onOpenDetail={() => navigate(href)}
                  onOpenComments={() => {
                    setCommentsListingId(String(l.public_id))
                    setCommentsOpen(true)
                  }}
                  onOpenGift={() => navigate(href)}
                />
              </div>
            )
          })}

          {!items && !errorMsg && (
            <div className="snap-start h-[100dvh] w-full relative bg-neutral-950">
              <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_20%,rgba(16,185,129,0.18),transparent_70%)]" />
              <div className="absolute left-0 right-0 bottom-0 p-6">
                <div className="glass rounded-2xl h-24" />
              </div>
            </div>
          )}
          {errorMsg && (
            <div className="snap-start h-[100dvh] w-full relative bg-neutral-950 flex items-center justify-center px-6">
              <div className="text-sm text-red-300">{errorMsg}</div>
            </div>
          )}
        </div>
      </div>

      {commentsListingId ? (
        <CommentsDrawer
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          target={{ type: 'listing', id: commentsListingId }}
        />
      ) : null}

      {/* Desktop + iPad (>= md): unchanged */}
      <main className="hidden md:block mx-auto max-w-6xl px-4 py-6">
        {/* chips row */}
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto whitespace-nowrap">
            {(() => {
              // Exactly one chip active at a time
              // all: no filters and newest sort; newest: newest with any filters; popular: popular
              const chip: 'all' | 'popular' | 'newest' =
                sort === 'popular' ? 'popular' : (!hasActiveFilters ? 'all' : 'newest')
              return ['All', 'Popular', 'Newest'].map((c) => {
                const key = c.toLowerCase() as 'all' | 'popular' | 'newest'
                const isActive = chip === key
                const nextSort = key === 'popular' ? 'popular' : 'newest'
                return (
                  <button
                    key={c}
                    onClick={() => setSort(nextSort)}
                    className={`px-2.5 py-1 text-xs md:text-sm rounded-full border ${
                      isActive ? 'bg-brand border-transparent' : 'bg-white/5 border-white/10'
                    }`}
                  >
                    {c}
                  </button>
                )
              })
            })()}
          </div>
        </div>

        {/* Popular products rail */}
        {!q && popular?.length && !hasActiveFilters ? (
          <section className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-extrabold">Popular Products</h3>
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Filters"
                  onClick={() => setFilterOpen(true)}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                  title="Filter Products"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                    <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z" />
                  </svg>
                </button>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  title="Coming soon"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand/40 px-3 py-1.5 text-xs md:text-sm font-semibold text-white shadow cursor-not-allowed opacity-80"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-white/90" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  List product
                  <span className="ml-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/80">
                    Coming soon
                  </span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {popular.map((l: any, i: number) => {
                const img = l.images?.[0]?.public_url || l.product?.thumbnail_media?.public_url
                const title = l.product?.title || 'Listing'
                const href = `/store/i/${l.public_id}`
                const cond = (l.condition || '').toString().toUpperCase()
                const badge = cond === 'NEW' || cond === 'USED' ? cond : null
                const handle = l.seller?.handle ? `@${l.seller.handle}` : null
                const meta = handle ? `Sold by ${handle}` : null
                const price = fmtPixe(l.price_pixe)
                return (
                  <PixeCard key={l.public_id ?? i} title={title} image={img || null} href={href} badge={badge} meta={meta} price={price} />
                )
              })}
            </div>
          </section>
        ) : null}

        <section id="store">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-lg font-extrabold">Browse Products</h3>
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                aria-label="Filters"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                title="Filter Products"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                  <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z" />
                </svg>
              </button>
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Coming soon"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand/40 px-3 py-1.5 text-xs md:text-sm font-semibold text-white shadow cursor-not-allowed opacity-80"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-white/90" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                List product
                <span className="ml-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/80">
                  Coming soon
                </span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {!items && !errorMsg && Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-white/5 h-60" />
            ))}
            {errorMsg && (
              <div className="col-span-full glass rounded-xl p-4">
                <div className="font-semibold">Store</div>
                <div className="text-white/80 text-sm mt-1">{errorMsg}</div>
                {errorMsg.toLowerCase().includes('sign in') && (
                  <div className="text-xs text-white/80 mt-3">
                    Dev tip: paste a JWT access token to preview listings.
                    <button
                      onClick={() => {
                        const t = prompt('Paste JWT access token')
                        if (t) {
                          setToken(t.trim())
                          setErrorLocal(null)
                          fetch('/api/pixe_store/store/listings/', { headers: authHeaders() })
                            .then(async (r) => {
                              if (!r.ok) throw new Error(`HTTP ${r.status}`)
                              return r.json()
                            })
                            .then(() => {/* hook refetch handles items */})
                            .catch((e) => setErrorLocal(e?.message || 'Failed to load listings'))
                        }
                      }}
                      className="ml-2 inline-flex items-center rounded-md bg-brand px-2.5 py-1.5 text-xs font-semibold"
                    >
                      Set Token
                    </button>
                  </div>
                )}
              </div>
            )}
            {(() => {
              // Extra client-side filter pass for reliability (mirrors mobile)
              const toNum = (v: any) => {
                if (v == null) return null
                const n = typeof v === 'number' ? v : parseFloat(String(v))
                return isFinite(n) ? n : null
              }
              const ql = (q || '').trim().toLowerCase()
              const cat = (filters.category || '').toString().trim().toLowerCase()
              const cond = filters.condition || null
              const inStockOnly = Boolean(filters.inStockOnly)
              const pMin = toNum(filters.priceMin)
              const pMax = toNum(filters.priceMax)

              let list = (items ?? []).slice()
              if (ql) {
                list = list.filter((l: any) => {
                  const title = l?.product?.title?.toLowerCase?.() || ''
                  const subtitle = l?.product?.subtitle?.toLowerCase?.() || ''
                  const catName = l?.product?.category?.name?.toLowerCase?.() || ''
                  return title.includes(ql) || subtitle.includes(ql) || catName.includes(ql)
                })
              }
              if (cat) {
                list = list.filter((l: any) => {
                  const v = (l?.product?.category?.slug || l?.product?.category?.name || '').toLowerCase()
                  return v ? v.includes(cat) : false
                })
              }
              if (cond) list = list.filter((l: any) => (l?.condition || '').toLowerCase() === cond)
              if (inStockOnly) {
                list = list.filter((l: any) => {
                  const stock = typeof l?.stock_qty === 'number' ? l.stock_qty : null
                  const reserved = typeof l?.reserved_qty === 'number' ? l.reserved_qty : 0
                  return stock != null ? stock - reserved > 0 : false
                })
              }
              if (pMin != null || pMax != null) {
                list = list.filter((l: any) => {
                  const price = toNum(l?.price_pixe)
                  if (price == null) return false
                  if (pMin != null && price < pMin) return false
                  if (pMax != null && price > pMax) return false
                  return true
                })
              }

              const srt = filters.sort
              if (srt === 'popular') list.sort((a: any, b: any) => (b?.likes_count || 0) - (a?.likes_count || 0))
              else if (srt === 'price_asc') list.sort((a: any, b: any) => (toNum(a?.price_pixe) ?? 1e15) - (toNum(b?.price_pixe) ?? 1e15))
              else if (srt === 'price_desc') list.sort((a: any, b: any) => (toNum(b?.price_pixe) ?? -1e15) - (toNum(a?.price_pixe) ?? -1e15))

              return list.map((l: any, i: number) => {
                const img = l.images?.[0]?.public_url || l.product?.thumbnail_media?.public_url
                const title = l.product?.title || 'Listing'
                const subtitle = l.product?.subtitle || ''
                const href = `/store/i/${l.public_id}`
                const cond = (l.condition || '').toString().toUpperCase()
                const badge = cond === 'NEW' || cond === 'USED' ? cond : null
                const handle = l?.seller?.handle ? `@${l.seller.handle}` : null
                const meta = handle ? `Sold by ${handle}` : null
                const price = fmtPixe(l.price_pixe)
                return (
                  <PixeCard key={l.public_id ?? i} title={title} subtitle={subtitle} image={img || null} href={href} badge={badge} meta={meta} price={price} />
                )
              })
            })()}
          </div>
        </section>
      </main>

      <StoreFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onApply={(next) => {
          setFilters(next)
          setSort((next.sort as any) === 'popular' ? 'popular' : 'newest')
          setFilterOpen(false)
          // Desktop only: bring results into view so it's obvious the filter applied.
          if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
            setTimeout(() => {
              const el = document.getElementById('store')
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 0)
          }
        }}
        onClear={() => {
          setFilters({ ...DEFAULT_STORE_FILTERS, sort: 'newest' })
          setSort('newest')
        }}
      />
    </div>
  )
}

function fmtPixe(p?: string | number | null): string | null {
  if (p === null || p === undefined) return null
  const n = typeof p === 'number' ? p : parseFloat(String(p))
  if (!isFinite(n)) return null
  return `${n.toFixed(2)} PIXE`
}
